/**
 * Shared Operations - Base Classes and Common Utilities
 * 
 * Provides the base operations class and common functionality used by all
 * operation classes for offline handling, conflict detection, and sync queue management.
 */

import { offlineDB } from '../database';
import { STORES } from '../schema';
import type { SyncQueueItem } from '../schema';

/**
 * Base operations class with common functionality
 */
export abstract class BaseOperations<T extends { id: string; updated_at: string }> {
  constructor(protected storeName: string) {}

  /**
   * Add offline metadata to a record
   */
  protected addOfflineMetadata(record: T, isNew = false): T & { _last_modified: string; _sync_status: 'pending' } {
    return {
      ...record,
      _last_modified: new Date().toISOString(),
      _sync_status: 'pending' as const,
      ...(isNew && { _offline_created: true }),
      ...(!isNew && { _offline_updated: true })
    };
  }

  /**
   * Get a record by ID
   */
  async get(id: string): Promise<T | null> {
    return offlineDB.get<T>(this.storeName, id);
  }

  /**
   * Get all records
   */
  async getAll(): Promise<T[]> {
    return offlineDB.getAll<T>(this.storeName);
  }

  /**
   * Create a new record
   */
  async create(record: Omit<T, 'id' | 'created_at' | 'updated_at'>): Promise<T> {
    const now = new Date().toISOString();
    const newRecord = {
      ...record,
      id: crypto.randomUUID(),
      created_at: now,
      updated_at: now
    } as unknown as T;

    const recordWithMetadata = this.addOfflineMetadata(newRecord, true);
    await offlineDB.put(this.storeName, recordWithMetadata);
    
    // Add to sync queue
    await this.addToSyncQueue(newRecord.id, 'create', recordWithMetadata);
    
    return recordWithMetadata;
  }

  /**
   * Update an existing record
   */
  async update(id: string, updates: Partial<T>): Promise<T | null> {
    const existing = await this.get(id);
    if (!existing) return null;

    const updatedRecord = {
      ...existing,
      ...updates,
      updated_at: new Date().toISOString()
    } as T;

    const recordWithMetadata = this.addOfflineMetadata(updatedRecord, false);
    await offlineDB.put(this.storeName, recordWithMetadata);
    
    // Add to sync queue
    await this.addToSyncQueue(id, 'update', recordWithMetadata);
    
    return recordWithMetadata;
  }

  /**
   * Delete a record
   */
  async delete(id: string): Promise<void> {
    const existing = await this.get(id);
    if (!existing) return;

    await offlineDB.delete(this.storeName, id);
    
    // Add to sync queue
    await this.addToSyncQueue(id, 'delete', { id });
  }

  /**
   * Add operation to sync queue
   */
  protected async addToSyncQueue(recordId: string, operation: 'create' | 'update' | 'delete', data?: Record<string, unknown>): Promise<void> {
    const queueItem: SyncQueueItem = {
      id: crypto.randomUUID(),
      table_name: this.storeName,
      record_id: recordId,
      operation,
      data,
      priority: this.getSyncPriority(operation),
      retry_count: 0,
      max_retries: 3,
      created_at: new Date().toISOString(),
      status: 'pending'
    };

    await offlineDB.put(STORES.SYNC_QUEUE, queueItem);
  }

  /**
   * Get sync priority for operation type
   */
  protected getSyncPriority(operation: 'create' | 'update' | 'delete'): number {
    switch (operation) {
      case 'create': return 2;
      case 'update': return 3;
      case 'delete': return 1; // Highest priority
      default: return 5;
    }
  }

  /**
   * Query records by index
   */
  async queryByIndex(indexName: string, query: IDBValidKey | IDBKeyRange, limit?: number): Promise<T[]> {
    try {
      const result = await offlineDB.queryByIndex<T>(this.storeName, indexName, query, limit);
      return result;
    } catch (error) {
      console.error(`Error querying ${this.storeName} by index ${indexName}:`, error);

      // If it's a missing index error, provide a helpful fallback
      if (error instanceof Error && error.message.includes('index was not found')) {
        console.warn(`Index ${indexName} not found for store ${this.storeName}, consider updating database schema`);
        // Return empty array as fallback to prevent crashes
        return [];
      }

      throw error;
    }
  }

  /**
   * Count records
   */
  async count(query?: IDBValidKey | IDBKeyRange): Promise<number> {
    return offlineDB.count(this.storeName, query);
  }
}
